import { useRef, useEffect } from 'react'

import { uiGet } from '../http'
/**
 * @function usePageHandler
 * @description 
A react hook for executing api calls. It will take a single api promise
 * or an array of api promises. It will call them in parallel after the first render.
 * It will wait for the call(s) and then call the supplied callback function with the response.
 * The busy cursor and error messages are handled by uiApiCalls.
 * The purpose of this hook allows the dev to not worry about async or await.
 * @param {object} usePageHandlerParams object parameters to usePageHandler.
 * @param {string | string[] | Promise<any>} usePageHandlerParams.service url string or array of urls or promise based api call.
 * @param {Function} usePageHandlerParams.callback This function will be called when the data is available as a parameter to the function.
 * @param {boolean} usePageHandlerParams.allowBusy Indicates whether to use the busy cursor.
 * @param {boolean} usePageHandlerParams.allowErrorMsg Indicates whether to display error messages.
 * @param {Function} usePageHandlerParams.errorMessageFunc Error message display function.
 * @param {Function} usePageHandlerParams.busyOverride Set busy cursor ref for the global loader override.
 * @returns {void} Nothing
 * 
 * General Usage: usePageHandler(apiPromiseCall(s), (data: any) => {
 * // Handle the data here for your component.
 * })
 */
const usePageHandler = ({
  service,
  callback,
  allowBusy = true,
  allowErrorMsg = true,
  errorMessageFunc,
  busyOverride
}) => {
  const mounted = useRef(false)
  const apiCallMade = useRef(false)

  const makeApiCalls = async () => {
    try {
      if (!service) {
        callback()
        return
      }
      const response = await uiGet({ service, allowBusy, allowErrorMsg, errorMessageFunc, setBusyOverride: busyOverride?.current?.setBusy, clearBusyOverride: busyOverride?.current?.clearBusy })
      // After the await, the component may not be mounted.
      if (mounted.current && callback !== undefined) {
        callback(response)
      }
    } catch (ex) {
      if (mounted.current) {
        if (!allowErrorMsg && callback !== undefined) {
          callback(ex)
        }
      }
    }
  }

  useEffect(() => {
    const makeCall = async () => {
      mounted.current = true
      await makeApiCalls()
    }
    if (busyOverride === undefined) {
      makeCall()
    }
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    const makeCall = async () => {
      mounted.current = true
      await makeApiCalls(busyOverride)
    }
    if (busyOverride && busyOverride.current && !apiCallMade.current) {
      apiCallMade.current = true
      makeCall()
    }
  }, [busyOverride?.current])
}

export default usePageHandler
